import { useState, useEffect, useCallback } from 'react';

/**
 * Hook para manejar Geolocation API con permisos y errores
 */
export const useGeolocation = (options = {}) => {
  const [location, setLocation] = useState(null);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const [isTracking, setIsTracking] = useState(false);
  const [watchId, setWatchId] = useState(null);

  // Opciones por defecto
  const geoOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 0,
    ...options
  };

  // Verificar si Geolocation está disponible
  const isGeolocationAvailable = 'geolocation' in navigator;

  // Verificar si estamos en HTTPS (requerido para Geolocation)
  const isSecureContext = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost';

  // Verificar permisos
  const checkPermission = useCallback(async () => {
    if (!navigator.permissions) {
      setPermission('unknown');
      return 'unknown';
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      setPermission(result.state);
      
      // Escuchar cambios en permisos
      result.addEventListener('change', () => {
        setPermission(result.state);
      });

      return result.state;
    } catch (err) {
      console.error('Error checking geolocation permission:', err);
      setPermission('unknown');
      return 'unknown';
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  // Solicitar permisos y obtener ubicación actual
  const requestLocation = useCallback(() => {
    if (!isGeolocationAvailable) {
      setError('Geolocation no está disponible en este navegador');
      return Promise.reject('Geolocation not available');
    }

    if (!isSecureContext) {
      setError('Geolocation requiere HTTPS o localhost');
      return Promise.reject('Secure context required');
    }

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const locationData = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            speed: position.coords.speed,
            heading: position.coords.heading,
            timestamp: position.timestamp
          };
          setLocation(locationData);
          setError(null);
          setPermission('granted');
          resolve(locationData);
        },
        (err) => {
          let errorMessage = 'Error desconocido';
          
          switch (err.code) {
            case err.PERMISSION_DENIED:
              errorMessage = 'Permiso de ubicación denegado. Por favor habilita el GPS en la configuración de tu navegador.';
              setPermission('denied');
              break;
            case err.POSITION_UNAVAILABLE:
              errorMessage = 'Información de ubicación no disponible. Verifica que el GPS esté activado.';
              break;
            case err.TIMEOUT:
              errorMessage = 'Tiempo de espera agotado para obtener ubicación.';
              break;
            default:
              errorMessage = err.message;
          }
          
          setError(errorMessage);
          reject(err);
        },
        geoOptions
      );
    });
  }, [isGeolocationAvailable, isSecureContext, geoOptions]);

  // Iniciar tracking continuo
  const startTracking = useCallback(() => {
    if (!isGeolocationAvailable || !isSecureContext) {
      setError('No se puede iniciar el tracking');
      return;
    }

    if (watchId !== null) {
      console.log('Tracking ya está activo');
      return;
    }

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          speed: position.coords.speed,
          heading: position.coords.heading,
          timestamp: position.timestamp
        };
        setLocation(locationData);
        setError(null);
        setIsTracking(true);
      },
      (err) => {
        let errorMessage = 'Error en tracking';
        
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Permiso de ubicación denegado';
            setPermission('denied');
            stopTracking();
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Ubicación no disponible';
            break;
          case err.TIMEOUT:
            errorMessage = 'Timeout obteniendo ubicación';
            break;
          default:
            errorMessage = err.message;
        }
        
        setError(errorMessage);
      },
      geoOptions
    );

    setWatchId(id);
    setIsTracking(true);
  }, [isGeolocationAvailable, isSecureContext, watchId, geoOptions]);

  // Detener tracking
  const stopTracking = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
      setIsTracking(false);
    }
  }, [watchId]);

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [watchId]);

  return {
    location,
    error,
    permission,
    isTracking,
    isGeolocationAvailable,
    isSecureContext,
    requestLocation,
    startTracking,
    stopTracking,
    checkPermission
  };
};